import styled from 'styled-components';
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import themeColors from '../../assets/styles/themeColors';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
`;

const TextInputField = styled.input`
  padding: 8px;
  border: 1px solid
    ${({ hasError }) =>
      hasError
        ? themeColors.colorErrorMessage
        : themeColors.colorBorderPrimary};
  border-radius: 4px;
  color: ${themeColors.colorTextSecondary};
  font-size: 0.9rem;
  outline: none;
  background: transparent;
  width: ${({ width }) => width ?? '100%'}; /* Apply width dynamically */
  height: ${({ height }) => height ?? 'auto'};

  &:focus {
    border-color: ${themeColors.colorSvgSemidark};
  }

  &:disabled {
    background-color: ${themeColors.colorBackgroundSecondaryLight};
  }

  @media (max-width: 900px) and (min-width: 700px) {
    width: 7.9rem;
  }
  @media (max-width: 768px) and (min-width: 600px) {
    width: 6rem; /* Applies between 576px - 768px */
  }

  @media (max-width: 620px) and (min-width: 578px) {
    width: 5rem; /* Applies between 576px - 768px */
  }

  @media (max-width: 475px) {
    width: 3.5rem;
    padding: 8px;
    font-size: 0.85rem;
  }
`;

const MaskedTextInputField = styled(TextInputField).attrs({
  as: 'MaskedInput',
})``;

const Label = styled.p`
  margin-top: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${themeColors.colorTextSecondary};
`;

export const ErrorMessage = styled.p`
  font-size: 0.8rem;
  margin-top: 2px;
  color: ${themeColors.colorErrorMessage};
`;

const InputNewText = forwardRef(
  (
    {
      label,
      maxLength,
      type,
      error,
      isMasked,
      mask,
      width, // New prop for responsive width
      autocomplete,
      autoFocus,
      onInput,
      ...props
    },
    ref,
  ) => {
    const Input = isMasked ? MaskedTextInputField : TextInputField;

    return (
      <InputContainer length={width}>
        <Input
          type={type}
          {...props}
          ref={ref}
          hasError={!!error}
          style={{ width }}
          maxLength={maxLength}
          autoComplete={autocomplete}
          autoFocus={autoFocus}
          onInput={onInput}
        />
        {!!label && <Label>{label}</Label>}
        {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
      </InputContainer>
    );
  },
);

InputNewText.propTypes = {
  isMasked: PropTypes.bool,
  mask: PropTypes.string,
  suggestion: PropTypes.string,
  label: PropTypes.string,
  width: PropTypes.string,
  type: PropTypes.oneOf(['text', 'password', 'email', 'number', 'tel']),
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  autocomplete: PropTypes.string,
  turnOff: PropTypes.bool,
  textAlign: PropTypes.string,
  fontSize: PropTypes.string,
  maxLength: PropTypes.string,
  autoFocus: PropTypes.bool,
  onInput: PropTypes.func,
};

InputNewText.defaultProps = {
  type: 'text',
  isMasked: false,
  turnOff: false,
};

InputNewText.displayName = 'InputText';

export default InputNewText;
